import Sortable from "sortablejs";
import { useCallback, useEffect, useRef, useState } from "react";
import { commitReorder, orderByIds, type Identified } from "../lib/reorder";

type ResolveOrder<T> = (items: T[], visibleIds: number[]) => T[];

interface Options<T extends Identified> {
  items: T[];
  rowSelector: string;
  onChange: (items: T[]) => void;
  onCommit: (items: T[]) => Promise<void>;
  onError: (error: unknown) => void;
  onCancel?: () => void;
  resolveOrder?: ResolveOrder<T>;
}

function rowIds(list: HTMLElement, selector: string): number[] {
  return [...list.querySelectorAll<HTMLElement>(selector)]
    .map(row => Number(row.dataset.reorderId))
    .filter(Number.isSafeInteger);
}

export function usePointerReorder<T extends Identified>({ items, rowSelector, onChange, onCommit, onError, onCancel, resolveOrder = orderByIds }: Options<T>) {
  const itemsRef = useRef(items); itemsRef.current = items;
  const onChangeRef = useRef(onChange); onChangeRef.current = onChange;
  const onCommitRef = useRef(onCommit); onCommitRef.current = onCommit;
  const onErrorRef = useRef(onError); onErrorRef.current = onError;
  const onCancelRef = useRef(onCancel); onCancelRef.current = onCancel;
  const resolveOrderRef = useRef(resolveOrder); resolveOrderRef.current = resolveOrder;
  const priorItems = useRef<T[] | null>(null);
  const priorVisibleIds = useRef<number[]>([]);
  const sortableRef = useRef<Sortable | null>(null);
  const busy = useRef(false);
  const mounted = useRef(true);
  const [list, setList] = useState<HTMLElement | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const listRef = useCallback((node: HTMLUListElement | null) => { setList(node); }, []);

  useEffect(() => {
    if (!list) return;
    const sortable = new Sortable(list, {
      handle: ".reorder-grip",
      draggable: rowSelector,
      dataIdAttr: "data-reorder-id",
      direction: "vertical",
      animation: 150,
      swapThreshold: 0.65,
      invertSwap: true,
      forceFallback: true,
      fallbackOnBody: true,
      fallbackTolerance: 3,
      touchStartThreshold: 3,
      ghostClass: "reorder-sortable-ghost",
      chosenClass: "is-dragging",
      dragClass: "reorder-drag-source",
      fallbackClass: "reorder-drag-ghost",
      removeCloneOnHide: true,
      onStart: event => {
        priorItems.current = itemsRef.current;
        priorVisibleIds.current = rowIds(list, rowSelector);
        busy.current = true;
        const id = Number(event.item.dataset.reorderId);
        if (mounted.current && Number.isSafeInteger(id)) setDraggingId(id);
      },
      onEnd: event => {
        const prior = priorItems.current ?? itemsRef.current;
        const priorIds = priorVisibleIds.current;
        priorItems.current = null;
        priorVisibleIds.current = [];
        if (mounted.current) setDraggingId(null);
        if (event.oldDraggableIndex === event.newDraggableIndex || event.newDraggableIndex === undefined) {
          busy.current = false;
          onCancelRef.current?.();
          return;
        }
        const next = resolveOrderRef.current(itemsRef.current, rowIds(list, rowSelector));
        onChangeRef.current(next);
        sortable.option("disabled", true);
        void commitReorder(next, prior, onCommitRef.current, restored => {
          if (sortableRef.current === sortable) sortable.sort(priorIds.map(String));
          if (mounted.current) onChangeRef.current(restored);
        }, error => {
          if (mounted.current) onErrorRef.current(error);
        }).finally(() => {
          busy.current = false;
          if (sortableRef.current === sortable) sortable.option("disabled", false);
        });
      },
    });
    sortableRef.current = sortable;
    return () => {
      if (sortableRef.current === sortable) sortableRef.current = null;
      sortable.destroy();
    };
  }, [list, rowSelector]);

  useEffect(() => () => { mounted.current = false; }, []);

  function cancel() {
    const prior = priorItems.current;
    if (!prior) return;
    sortableRef.current?.sort(priorVisibleIds.current.map(String));
    priorItems.current = null;
    priorVisibleIds.current = [];
    busy.current = false;
    onChangeRef.current(prior);
    onCancelRef.current?.();
    if (mounted.current) setDraggingId(null);
  }

  return { draggingId, listRef, isBusy: () => busy.current, cancel };
}
